import React from "react";

// Icons
import { LogoIcon } from "../../assets/img/icons";

// API call
import { apiClient } from "../../apiClient";

// Configs
import { DEFAULT_API_KEY, endpoints } from "../../configs";

import AuthButtons, {
  GoogleButton,
  LinkedInButton,
  OfficeButton,
  SalesforceButton
} from "../../components/authentication/AuthButtons";

import toast from "../../components/base/Toast";

// Helper
import { setCookie, clearCookie } from "../../lib/helper";

// Constants
import { PARTNER_SIGNUP_WELCOME_MESSAGE } from "../../partnerSignup/Constants";
import { isBadRequest } from "../../common/http";
import Terminology from "../../lib/Terminology";
import SystemName from "../../Constants/SystemName";

/**
 * Remove local storage when user signup with social auth
 */
function removeOAuthLocalStorage() {
  clearCookie("googleAuthToken");
  clearCookie("office365AuthToken");
  clearCookie("linkedinAuthToken");
  clearCookie("salesforceAuthToken");
}

class GetStarted extends React.Component {
  // Signup with Google OAuth
  signUpWithGoogle(googleToken, callback, actions) {
    removeOAuthLocalStorage();
    apiClient.defaults.headers.common.Authorization = DEFAULT_API_KEY;
    apiClient
      .get(
        `${endpoints().userAPI}/google/auth/details?googleToken=${googleToken}`
      )
      .then(response => {
        setCookie(
          "googleAuthToken",
          response.data.authToken ? response.data.authToken : ""
        );

        actions && actions.setUserDetails(response.data);
      })
      // .then(() => callback())
      .catch(error => {
        if (isBadRequest(error)) {
          let errorMessage;
          const errorRequest = error.response.request;
          if (errorRequest && errorRequest.response) {
            errorMessage = JSON.parse(errorRequest.response).message;
          }
          toast.error(error.response.data.message);
          console.error(errorMessage);
        }
      });
  }

  // Signup with Microsoft Office365
  signUpWithOffice365(office365Token, callback, actions) {
    removeOAuthLocalStorage();
    apiClient.defaults.headers.common.Authorization = DEFAULT_API_KEY;
    apiClient
      .get(
        `${
          endpoints().userAPI
        }/office365/auth/details?office365Token=${office365Token}`
      )
      .then(response => {
        setCookie(
          "office365AuthToken",
          response.data.authToken ? response.data.authToken : ""
        );

        actions && actions.setUserDetails(response.data);
      })
      // .then(() => callback())
      .catch(error => {
        if (isBadRequest(error)) {
          let errorMessage;
          const errorRequest = error.response.request;
          if (errorRequest && errorRequest.response) {
            errorMessage = JSON.parse(errorRequest.response).message;
          }
          toast.error(error.response.data.message);
          console.error(errorMessage);
        }
      });
  }

  // Signup with Linkedin
  signUpWithLinkedin(linkedInAccessToken, callback, actions) {
    removeOAuthLocalStorage();
    apiClient.defaults.headers.common.Authorization = DEFAULT_API_KEY;
    apiClient
      .get(
        `${
          endpoints().userAPI
        }/linkedin/auth/details?linkedInAccessToken=${linkedInAccessToken}`
      )
      .then(response => {
        setCookie(
          "linkedinAuthToken",
          response.data.authToken ? response.data.authToken : ""
        );

        actions && actions.setUserDetails(response.data);
      })
      // .then(() => callback())
      .catch(error => {
        if (isBadRequest(error)) {
          let errorMessage;
          const errorRequest = error.response.request;
          if (errorRequest && errorRequest.response) {
            errorMessage = JSON.parse(errorRequest.response).message;
          }
          toast.error(error.response.data.message);
          console.error(errorMessage);
        }
      });
  }

  // Signup with Salesforce
  signUpWithSalesforce(salesforceToken, callback, actions) {
    removeOAuthLocalStorage();
    apiClient.defaults.headers.common.Authorization = DEFAULT_API_KEY;
    apiClient
      .get(
        `${
          endpoints().userAPI
        }/salesforce/auth/details?salesforceToken=${salesforceToken}`
      )
      .then(response => {
        setCookie(
          "salesforceAuthToken",
          response.data.authToken ? response.data.authToken : ""
        );

        actions && actions.setUserDetails(response.data);
      })
      // .then(() => callback())
      .catch(error => {
        if (isBadRequest(error)) {
          let errorMessage;
          const errorRequest = error.response.request;
          if (errorRequest && errorRequest.response) {
            errorMessage = JSON.parse(errorRequest.response).message;
          }
          toast.error(error.response.data.message);
          console.error(errorMessage);
        }
      });
  }

  render() {
    let { partnerSignupWelcomeMessage } = this.props;
    const {
      marketplaceName,
      enableSalesforceLogin,
      enableOffice365Login,
      enableGoogleLogin,
      enableLinkedInLogin,
      linkedInClientId,
      linkedInRedirectUri
    } = this.props;

    partnerSignupWelcomeMessage = partnerSignupWelcomeMessage
      ? partnerSignupWelcomeMessage
      : PARTNER_SIGNUP_WELCOME_MESSAGE;

    if (this.props.currentStep !== this.props.step) {
      // Prop: The current step
      return null;
    }

    return (
      <div className="content-wrapper">
        <div className="title-wrapper d-flex align-items-center">
          <LogoIcon />{" "}
          <h2 className="h1-5 font-weight-bold mb-0 ml-3">
            {/* {partnerSignupWelcomeMessage} */}
            Welcome Partner!
          </h2>
        </div>

        {/* Content */}
        <div className="content lh-2 h5 font-weight-light">
          <p>
            The {marketplaceName} partner portal is where we share resources,
            track {Terminology.get(SystemName.REFERRAL)}, and where you can
            manage your partner directory profile.
          </p>

          <p>
            Let's get your account setup so we can update your partner directory
            profile and get you into our Partner Portal!
          </p>
        </div>
        <AuthButtons>
          {enableSalesforceLogin == "true" && (
            <SalesforceButton
              redirectUrl={"/partner-signup?section=basic_information"}
              signUpWithSalesforce={this.signUpWithSalesforce}
              eventKey="salesforce-signup"
              actions={this.props}
            />
          )}

          {enableGoogleLogin === "true" && (
            <GoogleButton
              redirectUrl={"/partner-signup?section=basic_information"}
              signUpWithGoogle={this.signUpWithGoogle}
              eventKey="google-signup"
              actions={this.props}
            />
          )}

          {enableOffice365Login === "true" && (
            <OfficeButton
              redirectUrl={"/partner-signup?section=basic_information"}
              signUpWithOffice365={this.signUpWithOffice365}
              eventKey="microsoft-office265-signup"
              actions={this.props}
            />
          )}

          {enableLinkedInLogin === "true" && (
            <LinkedInButton
              redirectUrl={"/partner-signup?section=basic_information"}
              signUpWithLinkedin={this.signUpWithLinkedin}
              eventKey="linkedin-signup"
              actions={this.props}
              linkedInClientId={linkedInClientId}
              linkedInRedirectUri={linkedInRedirectUri}
            />
          )}
        </AuthButtons>
        <div className="btn-wrapper mt-4-5">
          <button type="reset" className="resigter-with-my-email">
            Register with my Email Address
          </button>
        </div>
        {/* /.btn-wrapper */}
      </div>
    );
  }
}

export default GetStarted;
